import type { IPilotage } from "./types";
import { bullets, birdsFlocks, coins, pickups } from "./entities";
import { shaker } from "./shaker";
import { explosions, emitBlood, emitCoinCollect, emitTrail } from "./effects";
import { curtains } from "./curtains";
import { hud } from "./hud";
import { camera } from "./camera";
import {
  GRAVITY,
  LIFT,
  MAX_VY_UP,
  MAX_VY_DOWN,
  COPTER_ANGLE_FACTOR,
  COPTER_SPRITE_W,
  COPTER_SPRITE_H,
  GOD_MODE_DURATION,
  DAMAGE_HOLE_RADIUS,
  DEATH_HOLE_RADIUS,
  SMALL_EXPLOSION_COUNT,
  GAME_W,
  GAME_H,
  MAX_HEART_SLOTS,
} from "./data";
import { gameState } from "./state";
import { landscape } from "./terrain";
import { Pilot } from "./pilot";
import { AutoPilot } from "./autopilot";
import { dt } from "lib/loop";

import { setLiftVolume, playSoundAt, stopMusic } from "audio";
import { Asset } from "assets.gen";

export const copter = {
  wx: 0,
  y: GAME_H * 0.5,
  vy: 0,
  boost: false,
  lifes: 3,
  _pilot: null as IPilotage | null,
  _godTime: -1,
  _grainStep: 0,
  _t: 0,
  _trailAccum: 0,
  _deathTimer: 0,
  visible: true,
  angle: 0,
  flicker: false,

  get centre(): { x: number; y: number } {
    return { x: this.wx + COPTER_SPRITE_W * 0.5, y: this.y + COPTER_SPRITE_H * 0.5 };
  },

  reset(): void {
    this._pilot = gameState.emulation ? new AutoPilot() : new Pilot();
    this._t = 0;
    this._trailAccum = 0;
    this.visible = true;
    this.angle = 0;
    this.boost = false;
    this.vy = 0;
    this.wx = 0;
    this._godTime = -1;
    this._deathTimer = 0;
    this._trailAccum = 0;

    setLiftVolume(0, 0);

    const { top, bottom } = landscape.getPlaceOffsets(this.wx + COPTER_SPRITE_W * 0.5);
    this.y = top + (bottom - top) * 0.5;
    this.lifes = this._pilot.maxLifes;
  },

  changePilot(): void {
    this._pilot = gameState.emulation ? new AutoPilot() : new Pilot();
  },

  update(): void {
    if (gameState.pauseMode) return;

    if (gameState.started || gameState.emulation) {
      if (this._pilot) {
        if (this._pilot.controlUp) {
          this.boost = true;
          setLiftVolume(1, 0);
        }
        if (this._pilot.controlDown) {
          this.boost = false;
          setLiftVolume(0, 1);
        }
      }

      this.vy += (this.boost ? LIFT : GRAVITY) * dt;
      if (this.vy > MAX_VY_DOWN) this.vy = MAX_VY_DOWN;
      if (this.vy < -MAX_VY_UP) this.vy = -MAX_VY_UP;

      this.angle = -this.vy * COPTER_ANGLE_FACTOR;
      if (this.angle > 45) this.angle = 45;
      if (this.angle < -45) this.angle = -45;
      this._t += dt;

      this.y += this.vy * dt;
      this.wx += landscape.vx * dt;

      this._trailAccum += dt;
      if (this._trailAccum >= 2 / 30) {
        this._trailAccum -= 2 / 30;
        const exhaustColor = this.boost ? "hsl(200, 70%, 70%)" : "hsl(40, 50%, 50%)";
        emitTrail(this.wx + 1, this.y + COPTER_SPRITE_H / 2, landscape.vx, exhaustColor);
      }

      this.updateGod();

      if (!this.isGod()) {
        if (this.collideTerrain()) {
          this.dodge();
        }
        const bulletIdx = this.collideBullet();
        if (bulletIdx >= 0) {
          bullets.splice(bulletIdx, 1);
          this.damage();
        }
        this.collideBirds();
      }

      this.collideCoins();
      this.collidePickups();

      if (this.isGod()) {
        const { top: up, bottom: down } = landscape.getPlaceOffsets(this.centre.x);
        if (this.y + 8 > down) {
          this.y = down - 8;
          if (this.vy > 0) this.vy *= -0.5;
        }
        if (this.y - 8 < up) {
          this.y = up + 8;
          if (this.vy < 0) this.vy *= -0.5;
        }
      }

      if (this.y - 2 < 0) this.y = 2;
      if (this.y + COPTER_SPRITE_H > GAME_H) this.y = GAME_H - COPTER_SPRITE_H;

      if (gameState.emulation && this._pilot) {
        this._pilot.update(this.centre);
      }
    }

    if (this._deathTimer > 0) {
      this._deathTimer -= dt;
      if (this._deathTimer <= 0) {
        this.fatality();
      }
    }
  },

  collideTerrain(): boolean {
    const cx = this.centre.x | 0;
    const { top, bottom } = landscape.getPlaceOffsets(cx);
    return this.y < top || this.y + COPTER_SPRITE_H > bottom;
  },

  collideBullet(): number {
    const sx = this.wx;
    for (let i = 0; i < bullets.length; i++) {
      const b = bullets[i];
      if (sx < b.x + 2 && sx + COPTER_SPRITE_W > b.x && this.y < b.y + 2 && this.y + COPTER_SPRITE_H > b.y) {
        return i;
      }
    }
    return -1;
  },

  collideBirds(): void {
    const sx = this.wx;
    for (let i = birdsFlocks.length - 1; i >= 0; i--) {
      const flock = birdsFlocks[i];
      if (
        sx < flock.x + 9 &&
        sx + COPTER_SPRITE_W > flock.x &&
        this.y < flock.y + 6 &&
        this.y + COPTER_SPRITE_H > flock.y
      ) {
        emitBlood(flock.x + 4, flock.y + 3);
        playSoundAt(Asset.sfx_kick, camera.worldToScreenX(flock.x + 4), GAME_W, 0.3);
        birdsFlocks.splice(i, 1);
      }
    }
  },

  collideCoins(): void {
    const sx = this.wx;
    for (let i = coins.length - 1; i >= 0; i--) {
      const coin = coins[i];
      if (
        sx < coin.x + coin.w &&
        sx + COPTER_SPRITE_W > coin.x &&
        this.y < coin.y + coin.h &&
        this.y + COPTER_SPRITE_H > coin.y
      ) {
        emitCoinCollect(coin.x + coin.w * 0.5, coin.y + coin.h * 0.5);
        playSoundAt(Asset.sfx_coin_collect, camera.worldToScreenX(coin.x + coin.w * 0.5), GAME_W, 0.2);
        gameState.score += 10;
        coins.splice(i, 1);
      }
    }
  },

  collidePickups(): void {
    const sx = this.wx;
    for (let i = pickups.length - 1; i >= 0; i--) {
      const pickup = pickups[i];
      if (
        sx < pickup.x + pickup.w &&
        sx + COPTER_SPRITE_W > pickup.x &&
        this.y < pickup.y + pickup.h &&
        this.y + COPTER_SPRITE_H > pickup.y
      ) {
        if (pickup.type === "max_hp") {
          hud.addHeartSlot();
          hud.healOneHeart();
        } else {
          hud.healOneHeart();
        }
        if (this.lifes < MAX_HEART_SLOTS) this.lifes++;
        explosions.push({ x: pickup.x + pickup.w * 0.5, y: pickup.y + pickup.h * 0.5, count: 12, t: 0, big: false });
        playSoundAt(Asset.sfx_coin_collect, camera.worldToScreenX(pickup.x + pickup.w * 0.5), GAME_W, 0.4);
        pickups.splice(i, 1);
      }
    }
  },

  dodge(): void {
    const { top: up, bottom: down } = landscape.getPlaceOffsets(this.centre.x);
    const middle = (up + down) * 0.5;
    let hitY: number;
    let hitX: number;
    if (this.y + COPTER_SPRITE_H * 0.5 > middle) {
      if (this.vy > 0) this.vy *= -1;
      hitY = this.y + COPTER_SPRITE_H;
      hitX = this.centre.x;
    } else {
      if (this.vy < 0) this.vy *= -1;
      hitY = this.y;
      hitX = this.centre.x;
    }
    this.damage(hitX, hitY);
  },

  damage(hitX?: number, hitY?: number): void {
    const hx = hitX ?? this.centre.x;
    const hy = hitY ?? this.centre.y;
    if (!gameState.emulation) {
      this.lifes--;
      hud.changeHearts(-1);
    }
    if (this.lifes <= 0) {
      this.destroy();
    } else {
      playSoundAt(Asset.sfx_kick, camera.worldToScreenX(hx), GAME_W, 1);
      shaker.perform(0.02, 0.2);
      landscape.makeHole(hx, hy, DAMAGE_HOLE_RADIUS);
      explosions.push({ x: hx, y: hy, count: SMALL_EXPLOSION_COUNT, t: 0, big: false });
      this.godMode(GOD_MODE_DURATION);
    }
  },

  destroy(): void {
    gameState.started = false;
    gameState.active = false;
    stopMusic();
    setLiftVolume(0, 0);
    playSoundAt(Asset.sfx_explosion, camera.worldToScreenX(this.centre.x), GAME_W, 1);
    this.godMode(-1);
    this._deathTimer = 10 / 30;
  },

  fatality(): void {
    landscape.makeHole(this.centre.x, this.centre.y, DEATH_HOLE_RADIUS);
    explosions.push({ x: this.centre.x, y: this.centre.y, count: 80, t: 0, big: true });
    shaker.perform(0.06, 0.8);
    curtains.close(1, () => {
      gameState.showGameOver = true;
    });
  },

  godMode(duration: number): void {
    this._godTime = duration;
    if (duration < 0) {
      this.flicker = false;
    } else {
      this._grainStep = (6 * 0.2) / duration;
    }
  },

  isGod(): boolean {
    return this._godTime >= 0;
  },

  updateGod(): void {
    if (!this.isGod()) return;
    this._godTime -= dt;
    if (this._godTime <= 0) {
      this._godTime = -1;
      this.flicker = false;
    } else {
      this.flicker = ((this._t * 30) | 0) % 6 < 3;
    }
  },
};
